// App shell: composes every HUD widget and screen over the map. Reads only the services API.
//
//   ┌ TopBar ─────────────────────────────────────────────────────────────┐
//   │ (T) yields …                               date · turn   menu       │
//   ├──────────┐                                        ┌─ DiplomacyRibbon┤
//   │ Country  │                                        │  ◉ ◉ ◉ ◉        │
//   │ Panel    │              map (view layer)          │    Notification │
//   │ (drawer) │                                        │    Stack  ◉     │
//   ├──────────┴─┐  ┌──────── TimeSlider ────────┐  ┌───┴─────────┐      │
//   │ Minimap +  │  │ ▶ 2000 ··· ◆ ··· 2026      │  │  NEXT TURN  │      │
//   │ LensPanel  │  └────────────────────────────┘  └─────────────┘      │
//   └────────────┘
//   Full screens (Diplomacy, Victory, War, Log) and the Sources drawer stack above.

import './theme.css';
import './hud.css';
import './screens.css';

import { render, type ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Services } from '../services/api.ts';
import { CountryPanel } from './CountryPanel.tsx';
import { DiplomacyRibbon } from './DiplomacyRibbon.tsx';
import { DiplomacyScreen } from './DiplomacyScreen.tsx';
import { HoverTooltip } from './HoverTooltip.tsx';
import { LeaderSelect } from './LeaderSelect.tsx';
import { LoadingScreen } from './LoadingScreen.tsx';
import { LogPanel } from './LogPanel.tsx';
import { MinimapPanel } from './Minimap.tsx';
import { NextTurn } from './NextTurn.tsx';
import { NotificationStack } from './NotificationStack.tsx';
import { SourcesDrawer } from './SourcesDrawer.tsx';
import { TimeSlider } from './TimeSlider.tsx';
import { TopBar } from './TopBar.tsx';
import { VictoryScreen } from './VictoryScreen.tsx';
import { WarPanel } from './WarPanel.tsx';
import { ServicesContext } from './context.ts';
import { inOffice, perspectiveOf } from './nation.ts';
import { TipLayer } from './parts.tsx';
import { hudSize, sourceFocus, switcherOpen, tip } from './store.ts';

/**
 * Widgets the composition root adds without the UI importing their code. `sound` (the music
 * control) sits in the top bar during the game, and in the top-left corner before it.
 */
export interface HudSlots {
  sound?: ComponentChildren;
}

/**
 * Mount the HUD over the map (for the .ts composition root, which cannot write JSX).
 * `container` must be positioned and cover the map; the HUD only catches pointer events on
 * its own widgets. Returns an unmount function.
 */
export function mountHud(container: HTMLElement, services: Services, slots: HudSlots = {}): () => void {
  render(<Hud services={services} slots={slots} />, container);
  return () => render(null, container);
}

export function Hud({ services, slots = {} }: { services: Services; slots?: HudSlots }) {
  return (
    <ServicesContext.Provider value={services}>
      <HudRoot services={services} slots={slots} />
    </ServicesContext.Provider>
  );
}

/** The sound control before the top bar exists (loading and leader screens). */
function SoundDock({ slots }: { slots: HudSlots }) {
  return slots.sound ? <div class="wc-sound-dock">{slots.sound}</div> : null;
}

// The frame that closes the picker already pays for the snapshot and the leader; the full HUD
// is built one frame later so its render is not part of that same long frame.
function useBuilt(leader: string | null): boolean {
  const [built, setBuilt] = useState(leader !== null);

  useEffect(() => {
    if (leader === null) {
      setBuilt(false);
      return;
    }
    if (built) {
      return;
    }

    let timer = 0;
    const frame = requestAnimationFrame(() => {
      timer = window.setTimeout(() => setBuilt(true), 0);
    });
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [leader, built]);

  return built;
}

function HudRoot({ services, slots }: { services: Services; slots: HudSlots }) {
  const { state } = services;
  const rootRef = useHudSize();
  useHotkeys(services);

  const leader = state.leader.value;
  const built = useBuilt(leader);
  const loading = state.loading.value;
  const panel = state.panel.value;

  if (loading) {
    return (
      <div class="wc-hud" ref={rootRef}>
        <LoadingScreen />
        <SoundDock slots={slots} />
      </div>
    );
  }

  if (!leader) {
    return (
      <div class="wc-hud" ref={rootRef}>
        <LeaderSelect />
        <SoundDock slots={slots} />
        <SourcesDrawer />
        <TipLayer />
      </div>
    );
  }

  if (!built) {
    return <div class="wc-hud" ref={rootRef} />;
  }

  return (
    <div class={hudClass(services)} ref={rootRef}>
      <TopBar sound={slots.sound} />
      <DiplomacyRibbon />
      <NotificationStack />
      <MinimapPanel />
      <TimeSlider />
      <NextTurn />
      <CountryPanel />
      <HoverTooltip />
      {panel === 'diplomacy' ? <DiplomacyScreen /> : null}
      {panel === 'victory' ? <VictoryScreen /> : null}
      {panel === 'war' ? <WarPanel /> : null}
      {panel === 'log' ? <LogPanel /> : null}
      <SourcesDrawer />
      <TipLayer />
    </div>
  );
}

// Root classes: historical mode (sepia chrome), how many banner lines sit under the top bar,
// and whether the country drawer is open (short screens hide the minimap under it).
function hudClass({ state, world }: Services): string {
  const past = state.date.value < state.asOf.value;
  const id = state.leader.value;
  const p = id ? perspectiveOf(world, id) : null;
  const caption = p !== null && !inOffice(world, p);
  const country = state.selection.value?.iso3 ? 'wc-hud--country' : '';
  return ['wc-hud', past ? 'wc-hud--past' : '', past && caption ? 'wc-hud--banners2' : '', country].join(' ');
}

// UI scale: the HUD is laid out for 1600×900 and grows with bigger screens (2560×1440 → 1.6),
// never shrinking below 1 so text stays legible at 1280–1366 wide.
const UI_BASE_W = 1600;
const UI_BASE_H = 900;
const UI_SCALE_MAX = 1.6;
const UI_SCALE_STEP = 100;

function uiScale(w: number, h: number): number {
  const s = Math.min(w / UI_BASE_W, h / UI_BASE_H);
  return Math.round(Math.min(UI_SCALE_MAX, Math.max(1, s)) * UI_SCALE_STEP) / UI_SCALE_STEP;
}

// Track the container size, zoom the HUD to fit, and publish the unscaled size.
function useHudSize() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    const box = el?.parentElement;
    if (!el || !box) {
      return;
    }
    const observer = new ResizeObserver(() => {
      const scale = uiScale(box.clientWidth, box.clientHeight);
      el.style.zoom = String(scale);
      hudSize.value = { w: box.clientWidth / scale, h: box.clientHeight / scale, scale };
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  return ref;
}

// Enter / Shift+Enter: next turn. Esc: close the innermost layer. '=': strategic view.
function useHotkeys(services: Services): void {
  const { state, actions } = services;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Esc always closes, even from a focused slider.
      if (e.key === 'Escape') {
        closeInnermost(services);
        return;
      }

      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      // '=' toggles the strategic 2D view, like Civ.
      if (e.key === '=' && state.leader.value) {
        actions.setMode(state.mode.value === 'strategic' ? '3d' : 'strategic');
        return;
      }

      if (e.key !== 'Enter' || e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }
      // Buttons handle their own Enter; only the map/background advances the turn.
      if (target && target.tagName === 'BUTTON') {
        return;
      }
      if (!state.leader.value || state.panel.value !== 'none' || state.date.value >= state.asOf.value) {
        return;
      }
      e.preventDefault();
      void actions.nextTurn();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [services]);
}

function closeInnermost({ state, actions }: Services): void {
  tip.value = null;
  if (switcherOpen.value) {
    switcherOpen.value = false;
    return;
  }
  if (sourceFocus.value.length > 0) {
    sourceFocus.value = [];
    return;
  }
  if (state.panel.value !== 'none') {
    actions.openPanel('none');
    return;
  }
  if (state.selection.value) {
    actions.select(null);
  }
}
